// ./src/components/features/Billing/PlatformPayStep.tsx
//
// Paying the PLATFORM in a drawer, without leaving the page: Stripe's PaymentElement, as guests pay for tickets
// (`StripePayStep` in events-web). Shared by the top-up drawer and the event upgrade (ROADMAP #209) — moved out of
// `TopUpDrawer` unchanged, so both rails take money the same way.
//
// ⚠ PLATFORM money: no `stripeAccount` (a ticket sale passes the host's connected account; this must not, or the
//   platform's revenue would land with the host). ⚠ Stripe taking the money is NOT the grant — the caller confirms it
//   (`AppAddonService.confirmPayment`) and waits for the grant; ms_auth's webhook grants it too, whichever is first.
import React from "react";
import { Button, Group, Stack } from "@mantine/core";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";

import { useDrawerLeave } from "@/components/generic";

// One Stripe.js instance per publishable key, so re-renders don't reload it.
// ⚠ NO `stripeAccount`: this is PLATFORM money. A ticket sale passes the host's connected
// account; a top-up must not, or the platform's revenue would land with the host.
const stripeCache = new Map<string, ReturnType<typeof loadStripe>>();

export const getPlatformStripe = (publishableKey: string) => {
  let p = stripeCache.get(publishableKey);

  if (!p) {
    p = loadStripe(publishableKey);
    stripeCache.set(publishableKey, p);
  }

  return p;
};

/** The card form. Inside <Elements>, so it can use the Stripe hooks. */
export const PlatformPayStep: React.FC<{
  amountLabel: string | null;
  onPaid: () => void;
  onError: (message: string | null) => void;
  onBack: () => void;
  /** Whether the card form holds anything typed — Stripe's element is an iframe, so only its `onChange` can say. */
  onCardTyped: (typed: boolean) => void;
  /** A payment is being confirmed: nothing may close the drawer under it. */
  onSubmittingChange: (submitting: boolean) => void;
}> = ({ amountLabel, onPaid, onError, onBack, onCardTyped, onSubmittingChange }) => {
  const { t } = useTranslation(["events", "common"]);
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmittingState] = React.useState(false);
  // Back drops the card details typed here: it asks first, as closing the drawer does (#124).
  const leave = useDrawerLeave();
  // An answer Stripe gives AFTER this step is gone (the host closed a stalled payment, #126) reports nothing here: the
  // purchase it belonged to is over, and the webhook grants the credits whatever this screen does.
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setSubmitting = (value: boolean) => {
    setSubmittingState(value);
    onSubmittingChange(value);
  };

  const pay = async () => {
    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    onError(null);

    // ⚠ `if_required` keeps card payments in-page. Only a redirect-based method would
    // navigate, and that is exactly the case this whole rebuild exists to avoid — so nothing
    // here may assume the page survives; the webhook is what actually grants.
    const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });

    if (!mountedRef.current) {
      return;
    }

    if (error) {
      onError(error.message || t("events:composer.topUp.payFailed", {
        defaultValue: "Your payment could not be completed. Check the card details and try again.",
      }));
      setSubmitting(false);

      return;
    }

    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      // ⚠ `processing` counts too. Some methods settle asynchronously; the money is committed
      // and the webhook will grant when it lands. Treating it as a failure would tell a host
      // who has paid that they have not.
      onPaid();

      return;
    }

    onError(t("events:composer.topUp.payIncomplete", {
      defaultValue: "That payment did not complete. Nothing has been charged.",
    }));
    setSubmitting(false);
  };

  return (
    <Stack gap="md">
      <PaymentElement onChange={(e) => onCardTyped(!e.empty)} />

      <Group justify="space-between" wrap="wrap" gap="sm">
        <Button variant="default" onClick={() => leave(onBack)} disabled={submitting}>
          {t("common:back", { defaultValue: "Back" })}
        </Button>
        <Button onClick={pay} loading={submitting} disabled={!stripe || !elements}>
          {amountLabel
            ? t("events:composer.topUp.payAmount", { defaultValue: "Pay {{amount}}", amount: amountLabel })
            : t("events:composer.topUp.pay", { defaultValue: "Pay" })}
        </Button>
      </Group>
    </Stack>
  );
};
